"use client";

import { useLingui } from "@lingui/react/macro";
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import { PILE_WORDS, type PileWord } from "./content";
import { isNarrow, prefersReducedMotion, schedule } from "./motion";
import { useSectionInView } from "./section";

/**
 * The pile of jobs. When the Why section comes into view the chips drop in
 * one after another; after a beat most of them lift away and the three that
 * still need the owner settle together (a FLIP transition over the gap the
 * lifted ones leave). On narrow screens there is no drop and the lifted chips
 * just dim; under reduced motion the end state is shown at once. Replays on
 * re-entry.
 */

type Phase = "hidden" | "landed" | "lifting" | "settled";

const FALL_DELAY_MS = 90;
const LIFT_DELAY_MS = 55;
const LIFT_AFTER_MS = 2600;
const SETTLE_AFTER_MS = 1900;

const chipBase =
  "whitespace-nowrap rounded-[10px] bg-surface-elevated px-3.5 py-2 text-[15px] font-medium text-foreground-strong sm:px-4 sm:py-2.5 sm:text-[clamp(15px,1.25vw,18px)]";

// The delay rides inside the `transition` shorthand: React warns (and can
// mis-apply) when a shorthand and its longhand (`transitionDelay`) change
// together across renders.
const drop = (delayMs: number) =>
  `opacity 0.35s ease ${delayMs}ms, transform 0.75s cubic-bezier(0.3,1.35,0.5,1) ${delayMs}ms`;

export function Pile() {
  const { i18n, t } = useLingui();
  const inView = useSectionInView();
  const [phase, setPhase] = useState<Phase>("hidden");
  // Narrow/reduced-motion decide the choreography and are read when it starts.
  const [mode, setMode] = useState<"full" | "fade" | "still">("full");
  const chipRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const beforeRects = useRef<Map<number, DOMRect>>(new Map());

  // Scheduled, never set synchronously: the phases land on timers (the first
  // two a tick apart, so the "hidden" frame is painted before the drop), and
  // the cleanup (scrolled away, unmounted) empties the pile for next time.
  useEffect(() => {
    if (!inView) return;
    const still = prefersReducedMotion();
    const fade = !still && isNarrow();
    const { at, clear } = schedule();
    at(0, () => {
      setMode(still ? "still" : fade ? "fade" : "full");
    });
    at(40, () => {
      setPhase(still ? "lifting" : "landed");
    });
    if (!still) {
      at(LIFT_AFTER_MS, () => {
        setPhase("lifting");
      });
      if (!fade) {
        at(LIFT_AFTER_MS + SETTLE_AFTER_MS, () => {
          // Record where the keepers are before the lifted chips leave the flow.
          beforeRects.current = new Map(
            PILE_WORDS.flatMap((w, i) => {
              const el = chipRefs.current[i];
              return !w.lifted && el ? [[i, el.getBoundingClientRect()] as const] : [];
            }),
          );
          setPhase("settled");
        });
      }
    }
    return () => {
      clear();
      setPhase("hidden");
    };
  }, [inView]);

  // FLIP: the keepers have just jumped to their packed positions; move them
  // back to where they were without a transition, then let them slide.
  useLayoutEffect(() => {
    if (phase !== "settled") return;
    const moves = PILE_WORDS.flatMap((w, i) => {
      const el = chipRefs.current[i];
      const before = beforeRects.current.get(i);
      if (w.lifted || !el || !before) return [];
      const after = el.getBoundingClientRect();
      return [{ el, dx: before.left - after.left, dy: before.top - after.top, tilt: w.tilt }];
    });
    moves.forEach(({ el, dx, dy, tilt }) => {
      el.style.transition = "none";
      el.style.transform = `translate(${dx}px, ${dy}px) rotate(${tilt}deg)`;
    });
    // Force the browser to commit the offset positions before animating.
    void document.body.offsetWidth;
    const raf = requestAnimationFrame(() => {
      moves.forEach(({ el, tilt }) => {
        el.style.transition = "transform 0.6s cubic-bezier(0.2,0.7,0.1,1)";
        el.style.transform = `rotate(${tilt}deg)`;
      });
    });
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [phase]);

  const styleFor = (w: PileWord, i: number): CSSProperties => {
    const tilt = `rotate(${w.tilt}deg)`;
    if (mode === "still") {
      return { opacity: w.lifted ? 0.22 : 1, transform: tilt };
    }
    if (mode === "fade") {
      const lifted = phase !== "hidden" && phase !== "landed" && w.lifted;
      const delay = phase === "hidden" || lifted ? 0 : i * 40;
      return {
        opacity: phase === "hidden" ? 0 : lifted ? 0.22 : 1,
        transform: tilt,
        transition: `opacity 0.5s ease ${delay}ms`,
      };
    }
    if (phase === "hidden") {
      return { opacity: 0, transform: `translateY(-160px) ${tilt}`, transition: drop(0) };
    }
    if (phase === "landed" || !w.lifted) {
      return { opacity: 1, transform: tilt, transition: drop(i * FALL_DELAY_MS) };
    }
    if (phase === "lifting") {
      const delay = i * LIFT_DELAY_MS;
      return {
        opacity: 0,
        transform: "translateY(-120px) rotate(0deg) scale(0.9)",
        transition: `opacity 0.7s ease ${delay}ms, transform 1s cubic-bezier(0.2,0.7,0.1,1) ${delay}ms`,
      };
    }
    return { display: "none" };
  };

  const highlighted = phase === "lifting" || phase === "settled";

  return (
    <div
      role="img"
      aria-label={t`A pile of jobs that land on you: invoices, replies, bookings, follow-ups, receipts, payroll, quotes, reminders, reports, contracts, bookkeeping, scheduling, newsletters, supplier calls and chasing payments. Most of them are lifted away.`}
      className="relative flex w-full max-w-[560px] flex-wrap-reverse content-start justify-center gap-2 gap-x-1.5 border-b-2 border-border-subtle pb-1.5 pt-1.5 justify-self-center sm:min-h-[330px] sm:gap-x-2 sm:gap-y-2.5 sm:pt-5"
    >
      {PILE_WORDS.map((w, i) => (
        <span
          key={w.label.id}
          ref={(el) => {
            chipRefs.current[i] = el;
          }}
          aria-hidden="true"
          className={`${chipBase} ${w.narrowHidden ? "max-sm:hidden" : ""} ${
            highlighted && !w.lifted && mode === "full"
              ? "shadow-[0_0_0_1.5px_var(--color-signal),0_10px_22px_-16px_rgba(10,22,40,0.45)]"
              : "shadow-[0_0_0_1px_var(--border-subtle),0_10px_22px_-16px_rgba(10,22,40,0.45)]"
          }`}
          style={styleFor(w, i)}
        >
          {i18n._(w.label)}
        </span>
      ))}
    </div>
  );
}
